/*
src/data/ParamsManager.js
wgbh-skydiagram-testing
astro.unl.edu
2019-07-14
*/


import {CallbackMakerMixin} from '../utils/CallbackMakerMixin.js';

import {ForegroundObjectManager} from './ForegroundObjectManager.js';


/*

The ParamsManager class stores and manages the set of parameters used by a SkyDiagram
	instance. An instance of this class provides the authoritative store of live values
	for the testing app.

Note: the manager has _properties_, from which the _parameters_ are derived.

The ParamsManager is created by a Workspace.


Properties (* -> properties that use getParams and setParams for getting and setting):

	width, height						*
	horizon, peak, margin		*
	
	showTrack								*
	trackColor							*
	trackWidth							*
	trackCutoutSize					*
	trackPath								*

	sunImage										- either null or an SDImage
	sunPosition							*
	sunSize									*
	sunNoImageColor					*

	moonImage										- either null or an SDImage
	moonPosition						*
	moonSize								*
	moonNoImageColor				*
	
	groundImage									- either null or an SDImage

	dayTopColor							*
	dayBottomColor					*
	nightTopColor						*
	nightBottomColor				*

	skyTransitionBelowThreshold		*
	skyTransitionAboveThreshold		*
	skyLevelTransitionPower				*

	darkestShadingLevel						*
	shadingLevelTransitionPower		*

	foregroundObjects						- an array of ForegroundObjectManager instances;
																see comments to change callback


Callbacks:
	ready			- Called after initialization is done (but references still need to be resolved). Only
							applies to asynchronous init methods (i.e. database).
	change		- Called when any of the properties have changed. The data argument will
							be an object with these properties:
								newValues		- May be empty. An object whose key/value pairs correspond to the
															changed properties and their new values. Changes to the properties of
															individual foreground objects will not be reported in the newValues object,
															even though such changes will cause the change callback to be made. In
															that case the newValues object will be empty. Changes to the size or
															order of the foregroundObjects array will be reported in the newValues object.
								deltaParams	-	Either null, or an object suitable for passing to a SkyDiagram instance.

*/

const STORE_NAME = 'params';


export class ParamsManager {


	static upgradeDatabase(db) {
		db.createObjectStore(STORE_NAME, {autoIncrement: true});
		ForegroundObjectManager.upgradeDatabase(db);
	}

	static getDefaultDef() {
		return {
			sunImageId: 'current',
			moonImageId: 'current',
			groundImageId: 'current',
			foregroundObjects: [
				{	id:	'tree',
					name: 'tree',
					imageId: 'tree',
					x: 0.17,
					y: 0.05,
					offsetX: 0.5,
					offsetY: 1.0,
					width: 0.05,
				},
				{ id: 'house',
					name: 'house',
					imageId: 'house',
					x: 0.78,
					y: 0.03,
					offsetX: 0.5,
					offsetY: 0.95,
					width: 0.065,
				},
				{	id: 'lamp-pane-off',
					name: 'lamp pane off',
					imageId: 'lamp-pane-off',
					system: 'object',
					referenceId: 'house',
					x: 0.5,
					y: 0.2,
					offsetX: 0.5,
					offsetY: 0.0,
					width: 0.4,
				}, 
				{	id: 'lamp-pane-on',
					name: 'lamp pane on',
					imageId: 'lamp-pane-on',
					system: 'object',
					referenceId: 'house',
					x: 0.5,
					y: 0.2,
					offsetX: 0.5,
					offsetY: 0.0,
					width: 0.4,
					applyNightShading: false,
					shouldRestrictVisibilityByTime: true,
					timesVisible: [{beginHour: 18, endHour: 6}],
				}, 
				{	id: 'lamp-frame',
					name: 'lamp frame',
					imageId: 'lamp-frame',
					system: 'object',
					referenceId: 'house',
					x: 0.5,
					y: 0.2,
					offsetX: 0.5,
					offsetY: 0.0,
					width: 0.4,
				},
			],
		};
	}

	toString() {
		return 'ParamsManager';
	}

	constructor(workspace, initObj) {

		this._db = null;

		this._isReady = false;

		this._workspace = workspace;

		this._needsReferenceResolution = false;

		this._onForegroundObjectReady = this._onForegroundObjectReady.bind(this);

		// Default settings follow. These may be overwritten in _init* methods.

		// simpleParams contains the properties/parameters than can be copied by
		//	direct assignment: numbers, bools, and strings.
		// These properties use the getParams and setParams methods.
		this._simpleParams = {

			// Layout
			width: 800,
			height: 200,
			horizon: 0.05,
			peak: 0.8,
			margin: 0.1,

			// Track
			showTrack: true,
			trackColor: '#ffffff',
			trackWidth: 2,
			trackCutoutSize: 1.2,
			
			// Sun
			sunPosition: 0.4,
			sunSize: 0.05,
			sunNoImageColor: '#fbd449',

			// Moon
			moonPosition: 0.2,
			moonSize: 0.05,
			moonNoImageColor: '#e0e0e0',

			// Sky
			dayTopColor: '#155ac7',
			dayBottomColor: '#29abe2',
			nightTopColor: '#030305',
			nightBottomColor: '#323052',
			skyTransitionBelowThreshold: 0.15,
			skyTransitionAboveThreshold: 0.05,
			skyLevelTransitionPower: 2,
			darkestShadingLevel: 0,
			shadingLevelTransitionPower: 3,
		};

		// Although not part of _simpleParams, the trackPath property uses the
		//	getParams and setParams methods.
		this._trackPath = [{x: 0.3, y: 1.0}];
	
		// The sun, moon, and ground images are either SDImage instances or null.
		this._sunImage = null;
		this._moonImage = null;
		this._groundImage = null;

		// The foregroundObjects array contains instances of ForegroundObjectManager instances.
		this._foregroundObjects = [];

		if (typeof initObj === 'object') {
			if (initObj.hasOwnProperty('def')) {
				this._initWithDef(initObj.def);
			} else if (initObj.hasOwnProperty('database')) {
				let useDefaults = Boolean(initObj.useDefaults);
				this._initWithDatabase(initObj.database, useDefaults);
			} else {
				console.warn('ParamsManager\'s constructor received an initObj, but did not find any recognized options.');
			}
		}

		CallbackMakerMixin.apply(this);
	}

	getIsReady() {
		return this._isReady;
	}

	_initWithDatabase(db, useDefaults) {
		this._db = db;

		if (useDefaults) {
			this._initWithDef(ParamsManager.getDefaultDef());
			this._store();
		} else {
			let t = this._db.transaction(STORE_NAME, 'readonly');
			t.onabort = (e) => {
				console.error('The database transaction was aborted.');
			};
			let r = t.objectStore(STORE_NAME).get(0);
			r.onsuccess = (e) => {
				this._finishInitWithDatabase(e.target.result);
			};
			r.onerror = (e) => {
				console.error('Failed to load stored object from database. Error: '+e.target.errorCode);
			};
		}
	}

	_finishInitWithDatabase(dbObj) {
		// This method is very similar to _initWithDef. The only difference between
		//	the def object and dbObj is that the dbObj's foregroundObjects property
		//	is an array of id values.
		
		this._foregroundObjectsCountdown = 0;

		if (typeof dbObj === 'object') {
			for (const key in this._simpleParams) {
				if (dbObj.hasOwnProperty(key)) {
					this._simpleParams[key] = dbObj[key];
				}
			}
	
			if (dbObj.hasOwnProperty('trackPath')) {
				this._trackPath = this._copyTrackPathArray(dbObj.trackPath);
			}
	
			if (dbObj.hasOwnProperty('foregroundObjects')) {
				this._foregroundObjects = [];
				let fobjIDs = dbObj.foregroundObjects;
				for (let i = 0; i < fobjIDs.length; ++i) {
					let fobj = new ForegroundObjectManager(this._workspace, this, {database: this._db, id: fobjIDs[i]});
					fobj.registerCallback('ready', this._onForegroundObjectReady);
					this._foregroundObjects[i] = fobj;
				}
				this._foregroundObjectsCountdown = fobjIDs.length;
			}
	
			this._sunImage = null;
			this._moonImage = null;
			this._groundImage = null;
	
			this._initSunImageId			=	dbObj.hasOwnProperty('sunImageId')		? dbObj.sunImageId		: null;
			this._initMoonImageId			=	dbObj.hasOwnProperty('moonImageId')		? dbObj.moonImageId		: null;
			this._initGroundImageId		=	dbObj.hasOwnProperty('groundImageId')	? dbObj.groundImageId	: null;
	
			this._needsReferenceResolution = true;
		} else {
			console.warn('There was no params object in the database. Default values will be used.');
		}

		if (this._foregroundObjectsCountdown === 0) {
			this._isReady = true;
			this.makeCallback('ready');
		}

	}

	_onForegroundObjectReady() {
		this._foregroundObjectsCountdown -= 1;
		if (this._foregroundObjectsCountdown === 0) {
			this._isReady = true;
			this.makeCallback('ready');
		}
	}

	_getDBObj() {

		let dbObj = {};

		for (const key in this._simpleParams) {
			dbObj[key] = this._simpleParams[key];
		}

		dbObj.trackPath = this._copyTrackPathArray(this._trackPath);

		dbObj.foregroundObjects = [];
		for (let i = 0; i < this._foregroundObjects.length; ++i) {
			dbObj.foregroundObjects[i] = this._foregroundObjects[i].getId();
		}

		if (this._needsReferenceResolution) {
			if (this._initSunImageId !== null) {
				dbObj.sunImageId = this._initSunImageId;
			}

			if (this._initMoonImageId !== null) {
				dbObj.moonImageId = this._initMoonImageId;
			}

			if (this._initGroundImageId !== null) {
				dbObj.groundImageId = this._initGroundImageId;
			}

		} else {
			if (this._sunImage !== null) {
				dbObj.sunImageId = this._sunImage.getId();
			}
	
			if (this._moonImage !== null) {
				dbObj.moonImageId = this._moonImage.getId();
			}
	
			if (this._groundImage !== null) {
				dbObj.groundImageId = this._groundImage.getId();
			}
		}

		return dbObj;
	}

	_initWithDef(def) {

		for (const key in this._simpleParams) {
			if (def.hasOwnProperty(key)) {
				this._simpleParams[key] = def[key];
			}
		}

		if (def.hasOwnProperty('trackPath')) {
			this._trackPath = this._copyTrackPathArray(def.trackPath);
		}

		if (def.hasOwnProperty('foregroundObjects')) {
			this._foregroundObjects = [];
			let fobjDefs = def.foregroundObjects;
			for (let i = 0; i < fobjDefs.length; ++i) {
				let fobj = new ForegroundObjectManager(this._workspace, this, {def: fobjDefs[i]});
				this._foregroundObjects[i] = fobj;
			}
		}

		this._sunImage = null;
		this._moonImage = null;
		this._groundImage = null;

		this._initSunImageId			=	def.hasOwnProperty('sunImageId')		? def.sunImageId		: null;
		this._initMoonImageId			=	def.hasOwnProperty('moonImageId')		? def.moonImageId		: null;
		this._initGroundImageId		=	def.hasOwnProperty('groundImageId')	? def.groundImageId	: null;

		this._needsReferenceResolution = true;

		this._isReady = true;
	}

	resolveReferences() {
		// Must be called after instance creation.

		if (!this._needsReferenceResolution) {
			return;
		}

		// If the lookup methods fail they return null, which is OK.
		// The lookup methods accept null as an argument.
		// Passing true as the second argument skips callbacks.

		this.setSunImage(this._workspace.getSunImageBank().getImageWithId(this._initSunImageId), true);
		this.setMoonImage(this._workspace.getMoonImageBank().getImageWithId(this._initMoonImageId), true);
		this.setGroundImage(this._workspace.getGroundImageBank().getImageWithId(this._initGroundImageId), true);

		for (let i = 0; i < this._foregroundObjects.length; ++i) {
			this._foregroundObjects[i].resolveReferences();
		}

		this._needsReferenceResolution = false;
	}


	/*
	**	General Params Methods
	*/

	getParams(subset) {
		// Returns a complete or partial params object in the format
		//	expected by the SkyDiagram component.
		// If subset is not an object then a complete params object is
		//	returned.
		// If subset is an object then the properties of subset that
		//	are also properties of the params object will be set.
		// E.g.: if subset is originally {width: null}, then after
		//	calling subset will be {width: <width value>}.
		// The actual subset object is the object whose values are
		//	set and returned -- the object is re-used.
		// The original values of subset properties are ignored and lost.
		// subset properties that don't match parameter properties
		//	are not affected.

		if (typeof subset === 'object') {
			// Return a subset of params.

			for (const key in this._simpleParams) {
				if (subset.hasOwnProperty(key)) {
					subset[key] = this._simpleParams[key];
				}
			}

			if (subset.hasOwnProperty('trackPath')) {
				subset.trackPath = this.getTrackPath();
			}

			if (subset.hasOwnProperty('sunImageSrc')) {
				subset.sunImageSrc = this.getSunImageSrcParam();
			}

			if (subset.hasOwnProperty('moonImageSrc')) {
				subset.moonImageSrc = this.getMoonImageSrcParam();
			}

			if (subset.hasOwnProperty('groundImageSrc')) {
				subset.groundImageSrc = this.getGroundImageSrcParam();
			}

			if (subset.hasOwnProperty('foregroundObjects')) {
				subset.foregroundObjects = this.getForegroundObjectsParam();
			}

			return subset;

		} else {
			// Return a complete params object.

			let p = Object.assign({}, this._simpleParams);

			p.trackPath = this.getTrackPath();
			p.sunImageSrc = this.getSunImageSrcParam();
			p.moonImageSrc = this.getMoonImageSrcParam();
			p.groundImageSrc = this.getGroundImageSrcParam();
			p.foregroundObjects = this.getForegroundObjectsParam();

			return p;
		}
	}

	setParams(arg) {
		// This method allows setting one or more params at a time using
		//	a delta object as the argument.

		let needsCallback = false;
		let deltaParams = {};

		for (const key in this._simpleParams) {
			if (arg.hasOwnProperty(key)) {
				this._simpleParams[key] = arg[key];
				deltaParams[key] = arg[key];
				needsCallback = true;
			}
		}

		if (arg.hasOwnProperty('trackPath')) {
			this._trackPath = this._copyTrackPathArray(arg.trackPath);
			deltaParams.trackPath = this._copyTrackPathArray(arg.trackPath);
			needsCallback = true;
		}

		if (arg.hasOwnProperty('sunImageSrc')) {
			console.error('Setting sunImageSrc in setParams is not supported.');
		}

		if (arg.hasOwnProperty('moonImageSrc')) {
			console.error('Setting moonImageSrc in setParams is not supported.');
		}

		if (arg.hasOwnProperty('groundImageSrc')) {
			console.error('Setting groundImageSrc in setParams is not supported.');
		}

		if (arg.hasOwnProperty('foregroundObjects')) {
			console.error('Setting foregroundObjects in setParams is not supported.');
		}

		if (needsCallback) {
			this._makeChangeCallback({newValues: deltaParams, deltaParams: deltaParams});
		}
	}


	/*
	**	Track Path Methods
	**
	**	Use setParams to set.
	*/

	getTrackPath() {
		return this._copyTrackPathArray(this._trackPath);
	}

	_copyTrackPathArray(original) {
		let copy = [];
		for (let i = 0; i < original.length; ++i) {
			let pt = original[i];
			copy[i] = {x: pt.x, y: pt.y};
		}
		return copy;
	}


	/*
	**	Sun Image Methods
	*/

	getSunImage() {
		return this._sunImage;
	}

	setSunImage(image, noCallback) {
	
		if (image === this._sunImage) {
			return;
		}

		if (this._sunImage !== null) {
			this._sunImage.release();
		}

		this._sunImage = image;
		
		if (this._sunImage !== null) {
			this._sunImage.retain();
		}

		if (!noCallback) {
			let newValues = {sunImage: this._sunImage};
			let deltaParams = {sunImageSrc: this.getSunImageSrcParam()};
			this._makeChangeCallback({newValues: newValues, deltaParams: deltaParams});
		}
	}

	getSunImageSrcParam() {
		if (this._sunImage !== null) {
			return this._sunImage.getSrc();
		} else {
			return '';
		}
	}


	/*
	**	Moon Image Methods
	*/

	getMoonImage() {
		return this._moonImage;
	}

	setMoonImage(image, noCallback) {
	
		if (image === this._moonImage) {
			return;
		}

		if (this._moonImage !== null) {
			this._moonImage.release();
		}

		this._moonImage = image;
		
		if (this._moonImage !== null) {
			this._moonImage.retain();
		}

		if (!noCallback) {
			let newValues = {moonImage: this._moonImage};
			let deltaParams = {moonImageSrc: this.getMoonImageSrcParam()};
			this._makeChangeCallback({newValues: newValues, deltaParams: deltaParams});
		}
	}

	getMoonImageSrcParam() {
		if (this._moonImage !== null) {
			return this._moonImage.getSrc();
		} else {
			return '';
		}
	}


	/*
	**	Ground Image Methods
	*/

	getGroundImage() {
		return this._groundImage;
	}

	setGroundImage(image, noCallback) {
	
		if (image === this._groundImage) {
			return;
		}

		if (this._groundImage !== null) {
			this._groundImage.release();
		}

		this._groundImage = image;
		
		if (this._groundImage !== null) {
			this._groundImage.retain();
		}

		if (!noCallback) {
			let newValues = {groundImage: this._groundImage};
			let deltaParams = {groundImageSrc: this.getGroundImageSrcParam()};	
			this._makeChangeCallback({newValues: newValues, deltaParams: deltaParams});
		}
	}

	getGroundImageSrcParam() {
		if (this._groundImage !== null) {
			return this._groundImage.getSrc();
		} else {
			return '';
		}
	}


	/*
	**	Foreground Objects Methods
	*/

	getForegroundObjects() {
		return this._foregroundObjects.slice();
	}

	addNewForegroundObjectWithImage(image) {
		let fobj = new ForegroundObjectManager(this._workspace, this, {image: image});
		this._foregroundObjects.push(fobj);
		this._onForegroundObjectUpdate(fobj, fobj.getParams(), true);
		return fobj;
	}

	deleteForegroundObject(foregroundObject) {
		let index = this._foregroundObjects.indexOf(foregroundObject);
		if (index >= 0) {
			if (foregroundObject.getCanDelete()) {
				// _destroy releases any retained references, like the image, or a reference object.
				foregroundObject._destroy();
				this._foregroundObjects.splice(index, 1);
				this._onForegroundObjectUpdate(null, {}, true);
			} else {
				console.error('Foreground object can not be deleted.');
			}
		} else {
			console.error('Could not find foreground object to delete.');
		}
	}

	changeForegroundObjectIndex(foregroundObject, newIndex) {
		let oldIndex = this._foregroundObjects.indexOf(foregroundObject);
		if (oldIndex < 0) {
			console.error('Can not change a foreground object\'s index. It could not be found in the array.');
			return;
		}
		if (newIndex < 0 || newIndex >= this._foregroundObjects.length) {
			console.error('Can not change a foreground object\'s index. The index is out of range.');
			return;
		}
		if (oldIndex > newIndex) {
			this._foregroundObjects.splice(newIndex, 0, foregroundObject);
			this._foregroundObjects.splice(oldIndex + 1, 1);
			this._onForegroundObjectUpdate(null, {}, true);
		} else if (newIndex > oldIndex) {
			this._foregroundObjects.splice(newIndex + 1, 0, foregroundObject);
			this._foregroundObjects.splice(oldIndex, 1);
			this._onForegroundObjectUpdate(null, {}, true);
		}
	}

	getForegroundObjectWithId(id) {
		// Returns a reference to a ForegroundObjectManager, or null.
		if (id === null || id === '') {
			return null;
		}
		for (const fobj of this._foregroundObjects) {
			if (fobj._id === id) {
				return fobj;
			}
		}
		console.error('Failed to find foreground object by id.');
		return null;
	}

	getForegroundObjectsParam() {
		// Returns the full object for use in the foregroundObjects array.
		let fobjs = [];
		for (let i = 0; i < this._foregroundObjects.length; ++i) {
			fobjs[i] = {
				ID: this._foregroundObjects[i]._id,
				params: this._foregroundObjects[i].getParams(),
			};
		}
		return fobjs;
	}

	_onForegroundObjectUpdate(fobj, deltaParams, includeForegroundObjects) {
		// Called by a ForegroundObjectManager when a parameter has been updated.
		// Also called internally when the _foregroundObjects array has changed.
		// The delta object contains the params expected by the SkyDiagram component.

		// fobj may be null, which is the case when items have been removed from the
		//	foregroundObjects array, or the order of items has changed.

		// The includeForegroundObjects flag determines if the foregroundObjects array
		//	is included in the newValues object in the callback.
		// The array should be included only when it has changed in size or order (i.e. when
		//	the method is called from within ParamsManager).
		// It should not be included when just foreground object properties have changed (i.e.
		//	when the method is called from ForegroundObjectManager).

		// This method packages the change into a minimal params update in a way
		//	the SkyDiagram expects.
		// Recall that the foregroundObjects parameter must be an array of objects
		//	with ID (required) and params (optional, may be delta) properties.
		// If the foregroundObjects parameter is provided to the component, every
		//	foreground object must be represented, even if only by ID.
		// Example: to set foreground object C's x parameter to 1, use:
		//	skyDiagram.setParams({foregroundObjects: [{ID: "A"}, {ID: "B"}, {ID: "C", params: {x: 1}}] }); 

		let fo = [];
		for (let i = 0; i < this._foregroundObjects.length; ++i) {
			let o = {};
			o.ID = this._foregroundObjects[i]._id;
			if (this._foregroundObjects[i] === fobj) {
				o.params = deltaParams;
			}
			fo[i] = o;
		}	

		let newValues = {};
		if (includeForegroundObjects) {
			newValues.foregroundObjects = this.getForegroundObjects();
		}

		this._makeChangeCallback({newValues: newValues, deltaParams: {foregroundObjects: fo}});
	} 



	/*
	**	Change
	*/

	_makeChangeCallback(data) {
		this._store();
		this.makeCallback('change', data);
	}

	_store() {
		if (this._db === null) {
			return;
		}
		let dbObj = this._getDBObj();
		let t = this._db.transaction(STORE_NAME, 'readwrite');
		t.onabort = (e) => {
			console.error('The database transaction was aborted. Local storage space may not be available.');
		};
		let r = t.objectStore(STORE_NAME).put(dbObj, 0);
		r.onerror = (e) => {
			console.error('Failed to store object in database. Error: '+e.target.errorCode);
		};	
	}
}



